"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { Squash as Hamburger } from "hamburger-react";
import clsx from "clsx";
import { Tournament } from "@/types/tournament";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { utcTimeLocal } from "@/utils/time";
import { useDetectOutsideClick } from "@/hooks/useDetectOutsideClick";
import { getTournamentPath } from "@/utils/tournament-path";

interface MainNavProps {
  navLinks: { slug: string; name: string }[];
  tournaments?: Tournament[];
}

const NavItem = ({
  navLink,
  isActive,
  tournaments = [],
  onClick,
}: {
  navLink: { slug: string; name: string };
  isActive: boolean;
  tournaments?: Tournament[];
  onClick: () => void;
}) => {
  const pathname = usePathname();
  const isLeaderboardNavLink =
    navLink.name.toLocaleLowerCase() === "leaderboard";

  const containerRef = useRef<HTMLDivElement>(null);

  const [isLeaderboardOpen, setIsLeaderboardOpen] = useDetectOutsideClick({
    el: containerRef as any,
    initialState: false,
  });

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>,
  ) => {
    if (isLeaderboardNavLink && tournaments?.length > 1) {
      e.preventDefault();
      e.stopPropagation();
      setIsLeaderboardOpen((prev) => !prev);
      return;
    }
    onClick();
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center justify-between">
        <Link
          key={navLink.slug}
          href={navLink.slug}
          passHref
          className="flex justify-between w-full"
          onClick={handleNavClick}
        >
          <div
            className={clsx(
              `group cursor-pointer whitespace-nowrap text-tm-c-light
              transition-opacity duration-300
              xl:text-xs 2xl:text-[15px]`,
              {
                "opacity-60": isActive,
                "opacity-100": !isActive,
              },
            )}
          >
            <span>{navLink.name}</span>
            <span className="hidden xl:flex xl:h-0.5 xl:w-full xl:mt-1">
              <span className="w-full flex justify-end">
                <span
                  className={clsx(
                    "block h-0.5 bg-tm-c-brand transition-[width] duration-300",
                    {
                      "w-full": isActive,
                      "w-0 group-hover:w-full": !isActive,
                    },
                  )}
                />
              </span>
              <span className="w-full flex">
                <span
                  className={clsx(
                    "block h-0.5 bg-tm-c-brand transition-[width] duration-300",
                    {
                      "w-full": isActive,
                      "w-0 group-hover:w-full": !isActive,
                    },
                  )}
                />
              </span>
            </span>
          </div>
          {tournaments?.length > 1 && (
            <div
              aria-expanded={isLeaderboardOpen}
              className="flex justify-center -mt-1.5"
            >
              <div
                className={clsx(
                  "transition-transform duration-300 ease-in-out motion-reduce:transition-none",
                  { "rotate-180": isLeaderboardOpen },
                )}
              >
                <ExpandMoreIcon fontSize="medium" />
              </div>
            </div>
          )}
        </Link>
      </div>
      {tournaments?.length > 1 && (
        <div
          aria-hidden={!isLeaderboardOpen}
          className={clsx(
            `grid xl:contents
              transition-[grid-template-rows] duration-300 ease-in-out
              motion-reduce:transition-none`,
            isLeaderboardOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="min-h-0 overflow-hidden xl:contents">
            <div
              className={clsx(
                `mx-7 mt-10 min-h-0
              xl:mx-0 xl:p-7 xl:z-10 xl:bg-tm-c-gray-950
              xl:absolute xl:left-1/2 xl:-translate-x-1/2 xl:top-full
              xl:rounded-[10px] xl:border xl:border-tm-c-gray-300
              xl:min-w-[290px] xl:max-w-[80vw] xl:w-fit
              transition-[opacity,visibility] duration-300 ease-in-out
              motion-reduce:transition-none`,
                isLeaderboardOpen
                  ? "opacity-100 visible"
                  : "opacity-0 invisible pointer-events-none",
              )}
            >
              <span className="text-base normal-case">In progress</span>
              <div
                className="
                grid grid-rows-[1fr] opacity-100 m-5 mb-10 xl:m-0 xl:mt-4
                transition-[grid-template-rows,opacity] duration-300 ease-in-out
                motion-reduce:transition-none
              "
              >
                <div
                  className={clsx(
                    `
                    flex flex-col gap-x-5 text-base
                    xl:w-max min-h-0 overflow-hidden
                  `,
                    {
                      "xl:grid xl:grid-cols-2": tournaments?.length > 3,
                      "xl:grid xl:grid-cols-1": tournaments?.length <= 3,
                    },
                  )}
                >
                  {tournaments?.map((tournament) => {
                    const tournamentPath = getTournamentPath(tournament);
                    const isActiveTournament =
                      pathname.startsWith(tournamentPath);
                    return (
                      <Link
                        key={tournament.id}
                        href={`${tournamentPath}/leaderboard`}
                        onClick={() => {
                          onClick();
                          setIsLeaderboardOpen(false);
                        }}
                        className={clsx(
                          `
                        p-5 rounded-md xl:w-full
                      `,
                          {
                            "bg-tm-c-gray-800 opacity-100 cursor-default pointer-events-none xl:hover:none":
                              isActiveTournament,
                            "cursor-pointer  xl:hover:bg-tm-c-gray-800 xl:hover:opacity-50 transition-opacity duration-300":
                              !isActiveTournament,
                          },
                        )}
                      >
                        <span
                          className={clsx("text-sm tracking-wide normal-case")}
                        >
                          {tournament.name}
                        </span>
                        <div className="normal-case text-xs opacity-50">
                          <span>
                            {utcTimeLocal(
                              tournament.startDate,
                              "MMM. D, H:mm z",
                            )}
                          </span>
                          {" - "}
                          <span>
                            {utcTimeLocal(tournament.endDate, "MMM. D, H:mm z")}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MainNav = ({ navLinks, tournaments }: MainNavProps) => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const fullPath =
    pathname + (searchParams.size !== 0 ? `?${searchParams.toString()}` : "");

  // Mobile panel: items fade in staggered after the panel slides in, and
  // fade out in reverse order on close. Always visible on desktop (xl).
  const navItemCount = navLinks.length + 1;
  const navItemClassName = clsx(
    "transition-opacity duration-400 motion-reduce:transition-none",
    isNavOpen ? "opacity-100" : "max-xl:opacity-0",
  );
  const navItemStyle = (i: number) => ({
    transitionDelay: `${isNavOpen ? 700 + i * 100 : (navItemCount - 1 - i) * 100}ms`,
  });

  useEffect(() => {
    if (typeof document !== "undefined") {
      if (isNavOpen) {
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
      } else {
        document.body.style.overflow = "auto";
        document.body.style.position = "static";
        document.body.style.width = "";
      }
    }
  }, [isNavOpen]);

  return (
    <header>
      <div className="grid-container">
        <section
          className={`flex items-center
            col-start-1 col-span-4
            lg:col-start-2 lg:col-span-14
            xl:max-[1280px]:col-start-1 xl:max-[1280px]:col-span-30 xl:max-[1280px]:mx-10
            2xl:col-start-2 2xl:col-span-30
            xl:gap-10
          `}
        >
          <div
            className="relative flex items-center w-full justify-between bg-tm-c-dark py-5 md:py-6 z-4 xl:w-fit xl:justify-normal
              max-xl:before:content-[''] max-xl:before:absolute max-xl:before:inset-y-0 max-xl:before:left-1/2
              max-xl:before:w-screen max-xl:before:-translate-x-1/2 max-xl:before:bg-tm-c-dark max-xl:before:-z-10"
          >
            <Link
              href="/"
              onClick={() => {
                isNavOpen && setIsNavOpen(false);
              }}
            >
              <Image
                src="/icons/nextgolftour_logo.svg"
                alt="Next golf tour logo"
                width={100}
                height={65}
                priority
                className="object-contain w-22.5 md:w-25 h-auto"
              />
            </Link>

            <div className="xl:hidden">
              <Hamburger
                toggled={isNavOpen}
                toggle={setIsNavOpen}
                size={28}
                distance="sm"
              />
            </div>
          </div>

          <div
            className={clsx(
              `fixed top-0 right-0 z-3 xl:static xl:items-center
                text-[15px] tracking-[1.5px] uppercase leading-3.75 xl:text-tm-c-light
                h-screen w-full min-[768px]:!w-[450px] xl:w-auto xl:h-auto
                bg-tm-c-dark overflow-y-auto xl:bg-none xl:p-0 xl:overflow-y-visible
                xl:flex-1
                transition-[translate,visibility] ease-in-out
                motion-reduce:transition-none
              `,
              isNavOpen
                ? "max-xl:translate-x-0 duration-900"
                : "max-xl:translate-x-full max-xl:invisible duration-600",
            )}
          >
            <nav className="flex flex-col justify-between gap-10 min-h-full pt-30 px-9 pb-10 xl:min-h-0 xl:gap-0 xl:flex-row xl:items-start xl:p-0">
              <div className="flex flex-col gap-10 xl:gap-5 min-[1536px]:gap-7.5 xl:flex-row">
                {navLinks.map((l, i) => {
                  const isLeaderboardNavLink =
                    l.name.toLowerCase() === "leaderboard";
                  const twoOrMoreTournaments =
                    tournaments && tournaments?.length > 1;

                  return (
                    <div
                      key={l.slug}
                      className={navItemClassName}
                      style={navItemStyle(i)}
                    >
                      {isLeaderboardNavLink && twoOrMoreTournaments && (
                        <span className="xl:hidden flex w-full h-px bg-tm-c-gray-800 mb-5" />
                      )}
                      <NavItem
                        key={l.slug}
                        navLink={l}
                        isActive={
                          l.slug === fullPath ||
                          (l.slug === "/order-of-merit" &&
                            pathname.startsWith("/order-of-merit/"))
                        }
                        onClick={() => setIsNavOpen(false)}
                        tournaments={isLeaderboardNavLink ? tournaments : []}
                      />
                      {isLeaderboardNavLink && twoOrMoreTournaments && (
                        <span className="xl:hidden flex w-full h-px bg-tm-c-gray-800 mt-5" />
                      )}
                    </div>
                  );
                })}
              </div>

              <div
                className={clsx(navItemClassName, "pb-7.5 md:pb-20 xl:pb-0")}
                style={navItemStyle(navLinks.length)}
              >
                <a
                  href="https://support.trackmangolf.com/hc/en-us/sections/33816266493979"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Help center"
                  className="flex items-center gap-1 normal-case text-xs transition-opacity duration-300 opacity-100 hover:opacity-50 whitespace-nowrap"
                  style={{ transform: "unset" }}
                >
                  <HelpOutlineIcon fontSize="small" />
                  <span className="xl:max-[1535px]:hidden">Help center</span>
                </a>
              </div>
            </nav>
          </div>

          <div
            className={clsx(
              "fixed min-[1201px]:hidden inset-0 bg-black z-2 transition-opacity ease-in-out motion-reduce:transition-none",
              isNavOpen
                ? "opacity-70 duration-700"
                : "opacity-0 duration-500 pointer-events-none",
            )}
          />
        </section>
      </div>
    </header>
  );
};

export default MainNav;
